import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Comic } from "@/mocks/comics";

interface HeroCarouselProps {
  items: Comic[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((v) => (v + 1) % items.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  const go = (next: number) => setIndex((next + items.length) % items.length);

  return (
    <section
      className="relative w-full overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <img
        src="https://readdy.ai/api/search-image?query=Dreamy%20stylized%20abstract%20forest%20illustration%20with%20soft%20bokeh%20light%20particles%20and%20layered%20foliage%20silhouettes%2C%20violet%20and%20mint%20green%20gradient%20tones%2C%20magical%20misty%20fantasy%20atmosphere%2C%20artistic%20digital%20painting%2C%20elegant%20and%20airy&width=1600&height=900&seq=hero-bg-fairy&orientation=landscape"
        alt="妖精森林氛围背景"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/90 to-background-50/40"></div>

      <div className="relative mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-20">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div key={current.id} className="animate-fade-up flex flex-col gap-5">
            <h1 className="flex items-center gap-2 font-heading text-base text-primary-700 md:text-lg">
              <i className="ri-sparkling-2-fill"></i>
              妖精漫画 · 妖精题材的故事与资讯，都在这里
            </h1>

            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs text-background-50">
                <i className="ri-fire-fill"></i>
                本周焦点
              </span>
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                {current.category}
              </span>
              <span className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-900">
                {current.status}
              </span>
            </div>

            <h2 className="font-heading text-3xl leading-tight text-foreground-950 md:text-5xl">
              {current.title}
            </h2>

            <p className="max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">
              {current.intro}
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-primary-600"></i>
                {current.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-bookmark-line text-primary-600"></i>
                更新至第 {current.latestChapter} 话
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-star-fill text-accent-500"></i>
                {current.rating.toFixed(1)}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                查看漫画详情
              </Link>
              <Link
                to={`/category?cat=${encodeURIComponent(current.category)}`}
                className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-compasses-2-line"></i>
                浏览同类作品
              </Link>
            </div>
          </div>

          <div className="flex justify-center lg:justify-end">
            <Link
              to={`/detail/${current.id}`}
              key={`${current.id}-cover`}
              className="animate-fade-up group relative w-56 shrink-0 cursor-pointer md:w-64"
            >
              <div className="relative w-full overflow-hidden rounded-lg pt-[133%]">
                <img
                  src={current.cover}
                  alt={`${current.title} 封面`}
                  title={`${current.title} 妖精题材漫画`}
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-foreground-950/70 px-4 py-1 text-xs text-background-50 whitespace-nowrap">
                点击进入详情
              </span>
            </Link>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到 ${item.title}`}
                onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all cursor-pointer ${
                  i === index ? "w-8 bg-primary-500" : "w-2 bg-primary-200 hover:bg-primary-300"
                }`}
              ></button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="上一部"
              onClick={() => go(index - 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              aria-label="下一部"
              onClick={() => go(index + 1)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-background-300 bg-background-50 text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}